/**
 * Shared helpers for renderer-driven micro-animation — the "juice" layer.
 *
 * Games stay state-driven: a renderer diffs the game state against what it drew
 * last frame, starts a tween for whatever changed, and keeps re-rendering itself
 * until every tween lands. Nothing in the game or input layer knows about it.
 *
 * See docs/juice.md.
 */

export type Easing = (t: number) => number;

/** Fast out of the gate, settling at the end — placement and arrival. */
export const easeOutCubic: Easing = (t) => 1 - Math.pow(1 - t, 3);

/** Slow to leave, whipping away — removal and exits. */
export const easeInCubic: Easing = (t) => t * t * t;

/** Symmetric — travel between two resting states. */
export const easeInOutCubic: Easing = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/** No easing. For something already in motion before the animation starts and still
 *  in motion after — a simulated path being played back, not a piece setting off. */
export const linear: Easing = (t) => t;

/**
 * Overshoots the target and settles back. `overshoot` is the classic Back
 * constant: 1.70158 overshoots ~10%, lower values are subtler. Keep it small on
 * a board where the final position carries meaning.
 *
 * The endpoints are returned exactly rather than evaluated. The polynomial is
 * `1 - (overshoot + 1) + overshoot` at t = 0, which cancels to a few ulps *below*
 * zero in floating point — and callers scale radii and sizes by this, where a
 * value of -2e-16 is a negative radius and a thrown DOMException.
 */
export function easeOutBack(t: number, overshoot: number = 1.2): number {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const c = overshoot + 1;
    return 1 + c * Math.pow(t - 1, 3) + overshoot * Math.pow(t - 1, 2);
}

/**
 * Signed turn from one angle to another, taking the short way round. A half-turn
 * has no short way, so `spin` decides: +1 turns clockwise (canvas angles grow
 * clockwise), -1 anticlockwise. Pass the direction the piece's own logic turns —
 * a flip that goes the way the player's action goes reads as deliberate.
 *
 * Angles are meant to be accumulated rather than renormalised: add the delta to a
 * running value, and a piece re-aimed mid-turn bends its motion instead of
 * restarting it.
 */
export function turnDelta(from: number, to: number, spin: number): number {
    let d = (to - from) % (2 * Math.PI);
    if (d > Math.PI) d -= 2 * Math.PI;
    if (d < -Math.PI) d += 2 * Math.PI;
    if (Math.abs(Math.abs(d) - Math.PI) < 1e-6) d = spin * Math.PI;
    return d;
}

/**
 * Linearly interpolate between two hex colours, returning an `rgb()` string.
 * Feed it eased progress, not raw `t`, so the colour travels with the shape.
 */
export function lerpColor(a: string, b: string, t: number): string {
    const pa = parseHexColor(a);
    const pb = parseHexColor(b);
    const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
    const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
    const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
    return `rgb(${r},${g},${bl})`;
}

function parseHexColor(h: string): [number, number, number] {
    const s = h.replace('#', '');
    return [
        parseInt(s.slice(0, 2), 16),
        parseInt(s.slice(2, 4), 16),
        parseInt(s.slice(4, 6), 16)
    ];
}

// ── Motion vocabulary ─────────────────────────────────────────────────
//
// Four roles, and every animation in the app picks one rather than inventing
// its own curve and duration. What a motion *means* — a piece arriving, a piece
// changing in place, a piece leaving — should look the same in every game, so
// the player learns the language once. See docs/juice.md for the reasoning and
// for how far each of these has actually been validated.

export type MotionRole = 'enter' | 'change' | 'exit' | 'settle' | 'emphasis' | 'replay';

export interface Motion {
    /** Curve applied to raw 0..1 progress. */
    ease: Easing;
    /** Target duration, ms. */
    ms: number;
}

export const MOTION: Record<MotionRole, Motion> = {
    /** A piece the player just added arrives: quick off the mark, then settles. */
    enter: { ease: easeOutCubic, ms: 150 },
    /** A piece already on the board changes in place — it overshoots and lands. */
    change: { ease: (t) => easeOutBack(t), ms: 170 },
    /** A piece leaves: reluctant, then gone. Shorter than `enter` — an exit has
     *  nothing left to tell you, so it shouldn't hold up what comes next. */
    exit: { ease: easeInCubic, ms: 130 },
    /** A value moving between two resting states, with no arrival or departure
     *  to punctuate: symmetric, no overshoot. */
    settle: { ease: easeInOutCubic, ms: 160 },
    /**
     * An outcome landing — a target cleared, a goal met. Punctuation rather than
     * information, so it gets a bigger overshoot than `change` and a little longer
     * to swing through it. Its length isn't a delay: the curve is past full size
     * within the first half, and the rest is the settle.
     */
    emphasis: { ease: (t) => easeOutBack(t, 1.7), ms: 220 },
    /**
     * Simulation played back inside the cell the player just touched, to show what
     * their piece *does* there. The odd one out, deliberately:
     *
     * - **Constant speed.** What's being replayed was already moving before the
     *   animation began and is still moving after it; easing it would say the
     *   opposite.
     * - **Slower than everything else**, past the ~250ms the other roles keep
     *   under. Those roles are the board answering, so their length is time the
     *   player waits. This isn't an answer — the answer already landed instantly —
     *   it's an explanation running alongside, and it has to be slow enough to
     *   actually read.
     */
    replay: { ease: linear, ms: 360 },
};

export interface TweenPhase {
    /** Raw progress, 0..1. Test this for completion, not `value`. */
    t: number;
    /** Eased progress. May exceed 1 mid-flight where the curve overshoots. */
    value: number;
    done: boolean;
}

/**
 * One pending animation frame at a time, plus an on/off switch.
 *
 * Renderers own an instance and call `request()` at the end of a render that
 * still has a tween running. `setEnabled(false)` is for *static scenes* — the
 * tutorial and the Scoring-rules overlay draw annotations on top of the board,
 * and a self-scheduled repaint would wipe them. Disabled, tweens are dropped and
 * everything snaps to its final state.
 */
export class TweenScheduler {
    private rafId: number | null = null;
    private enabled: boolean = true;

    /** Milliseconds on the same clock tween start times are recorded on. */
    now(): number {
        return typeof performance !== 'undefined' ? performance.now() : 0;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    /** Turning animation off also cancels any frame already scheduled. */
    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        if (!enabled) this.cancel();
    }

    /** Schedule `callback` on the next frame; a no-op if one is already pending. */
    request(callback: () => void): void {
        if (!this.enabled) return;
        if (this.rafId !== null) return;
        if (typeof requestAnimationFrame === 'undefined') return;
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            callback();
        });
    }

    cancel(): void {
        if (this.rafId === null) return;
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.rafId);
        this.rafId = null;
    }

    /**
     * Progress of a tween that started at `start`, clamped to 0..1. Start times
     * may sit in the future (staggered fan-outs), which reads as 0.
     */
    progress(start: number, durationMs: number, now: number = this.now()): number {
        if (now <= start) return 0;
        return Math.min(1, (now - start) / durationMs);
    }

    /**
     * Where a tween started at `start` sits this frame, under one of the `MOTION`
     * roles. The normal way to read a tween — renderers shouldn't be choosing
     * their own curve and duration.
     */
    tween(start: number, motion: Motion, now: number = this.now()): TweenPhase {
        const t = this.progress(start, motion.ms, now);
        return { t, value: motion.ease(t), done: t >= 1 };
    }
}

/**
 * Stagger delays for a group of elements changing at once, so a bulk change
 * (reset, restoring a best solution) fans out instead of popping as one block.
 * Returns the delay for element `index` of `count`, capped so the whole fan-out
 * stays inside `totalMs` however many elements there are.
 */
export function staggerDelay(
    index: number, count: number, stepMs: number = 30, totalMs: number = 210
): number {
    if (count <= 1) return 0;
    return index * Math.min(stepMs, totalMs / (count - 1));
}

// ── Keyed enter/exit families ─────────────────────────────────────────
//
// The plainest shape a renderer's diff can take: a family of pieces, each one
// either on the board or not, keyed by *where it sits*. Nearly every game in the
// sweep does this by hand for its one kind of piece, which is fine; `PieceSet`
// exists for the games that have several kinds and would otherwise repeat it
// (Antennas and Jumps: a body link, a landing, a launcher).
//
// Keying by position rather than by object id is the point, not an incidental
// choice — it is what makes a piece that changes hands stay the same piece.

export interface PieceTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
}

/** Where a piece sits in the fan-out of a bulk change, growing and shrinking. A
 *  run that arrives from one end leaves from the other, so the two are mirrored. */
export interface Ranked {
    enterRank: number;
    exitRank: number;
}

/**
 * One family of keyed pieces: which of them were on the board last frame, and a
 * tween for everything that has changed since.
 */
export class PieceSet {
    /** What the last frame drew. */
    state = new Set<string>();
    private tweens = new Map<string, PieceTween>();
    private seeded = false;

    constructor(private readonly scheduler: TweenScheduler) {}

    /** Drop everything in flight; the next frame draws the family at rest. */
    clear(): void {
        this.tweens.clear();
    }

    /**
     * Diff `next` against the last frame, tweening each change in `rank` order.
     * Returns the keys that arrived, for anything that needs to know.
     *
     * A first frame adopts the board as-is — otherwise opening a grid with a saved
     * solution replays the whole thing.
     */
    sync(next: Set<string>, rank: (key: string, entering: boolean) => number): Set<string> {
        if (!this.scheduler.isEnabled() || !this.seeded) {
            this.tweens.clear();
            this.state = next;
            this.seeded = true;
            return new Set();
        }

        const added = [...next].filter(k => !this.state.has(k));
        const removed = [...this.state].filter(k => !next.has(k));
        if (added.length === 0 && removed.length === 0) {
            this.state = next;
            return new Set();
        }

        const changed = [
            ...added.map(key => ({ key, entering: true })),
            ...removed.map(key => ({ key, entering: false }))
        ].sort((x, y) => rank(x.key, x.entering) - rank(y.key, y.entering));

        const now = this.scheduler.now();
        changed.forEach(({ key, entering }, i) => {
            this.tweens.set(key, {
                kind: entering ? 'enter' : 'exit',
                start: now + staggerDelay(i, changed.length)
            });
        });

        this.state = next;
        return new Set(added);
    }

    /**
     * How much of each moving piece is on the board this frame, 0..1. Advances and
     * retires the tweens, so it runs once per render and every drawing pass reads
     * the result. Pieces at rest aren't in it: on the board they are simply whole,
     * and off it they are gone.
     */
    frames(): Map<string, number> {
        const out = new Map<string, number>();
        const now = this.scheduler.now();
        for (const [key, tween] of this.tweens) {
            const { value, done } = this.scheduler.tween(tween.start, MOTION[tween.kind], now);
            if (done) { this.tweens.delete(key); continue; }
            // The `exit` curve runs the *disappearance*, so invert it.
            out.set(key, tween.kind === 'enter' ? value : 1 - value);
        }
        return out;
    }
}

/**
 * Forget whatever a renderer remembers about pieces that are neither on the board
 * nor still leaving it. The memory exists for the pieces on their way out, which
 * are already gone from game state and can't be looked up any more.
 */
export function pruneMemo(
    memo: Map<string, unknown>, state: Set<string>, frame: Map<string, number>
): void {
    for (const key of memo.keys()) {
        if (!state.has(key) && !frame.has(key)) memo.delete(key);
    }
}
